import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { matchPath, useLocation, useRouteMatch } from 'react-router-dom'

import client from 'api/client'
import { isCertified } from 'api/clients/WorkspaceDomainClientUtils'
import { useAuth } from 'hooks/UseAuth'
import { useCurrentArticleMeta } from 'hooks/useCurrentArticleMeta'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useWorkspace } from 'hooks/useWorkspace'
import {
  CONTRIBUTORS_PATH,
  CORE_PATH,
  DOC_PATH,
  PROFILE_PATH,
  REQUESTED_DOCS_PATH,
  WORKSPACE_JOIN_REQUEST_PATH,
  WORKSPACE_URL_JOIN,
  WRITE_PATH,
} from 'paths'
import { authMerge, currentWorkspaceIdSet, workspacesSet } from 'redux/actions'
import { Auth, Workspace } from 'types'
import { ALMANAC_CORE_WORKSPACE, ALMANAC_CORE_WORKSPACE_ID } from 'utils/constants'
import {
  redirectToCustomDomainIfNecessary,
  redirectToFrontendDomainIfNecessary,
  redirectToSubdomainIfNecessary,
} from 'utils/workspaceDomainUtils'

import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useIsCustomPath } from './useIsCustomPath'
import { usePathWorkspace } from './usePathWorkspace'

const defaultDomainWorkspacePaths = [
  CONTRIBUTORS_PATH,
  REQUESTED_DOCS_PATH,
  CORE_PATH,
  WORKSPACE_URL_JOIN,
  PROFILE_PATH,
  WORKSPACE_JOIN_REQUEST_PATH,
  WRITE_PATH,
]

const doesAuthWorkspaceMatch = (auth: Auth, workspace: Workspace) => {
  return (
    auth.company_id === workspace.id &&
    auth.company_key === workspace.key &&
    auth.company_name === workspace.name &&
    auth.admin === workspace.admin &&
    auth.paying === workspace.paying
  )
}

export function useWorkspaceRedirect(): { workspace?: Workspace; workspaceId: number; resolved: boolean } {
  const dispatch = useDispatch()
  const auth = useAuth()
  const location = useLocation()
  const [resolved, setResolved] = useState(false)
  const loggedIn = !!auth
  const docRouteMatch = useRouteMatch(DOC_PATH)
  const defaultDomainWorkspacePathsMatches = matchPath(location.pathname, { path: defaultDomainWorkspacePaths })
  const articleMeta = useCurrentArticleMeta() // TODO: Get rid of this, rely solely on usePathWorkspace
  const pathWorkspace = usePathWorkspace()

  // useCurrentWorkspace use redux store that is updated by children components
  // it can return undefined when the user doesn't have access to the workspace (guest user)
  const reduxWorkspace = useCurrentWorkspace()
  // NOTE: Fallback to the auth?.last_workspace_id in case no workspace loaded
  //    Otherwise when the above is empty we will attempt to use ALMANAC_CORE_WORKSPACE_ID with is guaranteed to mismatch
  //    Mismatching forces us to use the first workspace of the list on new windows and new tabs, last_workspace_id is much better
  // NOTE: We can't use: useSelector(({ workspace }: RootState) => workspace.currentWorkspaceId) (see #7680)
  //    There's code like `dispatch(currentWorkspaceIdSet(newHandbook.workspace_id))` in `CustomPathWrapper` that can only work if
  //    workspace.currentWorkspaceId is overriden by ALMANAC_CORE_WORKSPACE_ID
  const lastWorkspaceId = auth?.last_workspace_id || auth?.company_id

  // NOTE: almanac core is a fallback to render the children, even if not the correct workspace id, it will be reset
  // in useEffect hooks later on
  const [workspaceId, setWorkspaceId] = useState<number>(
    pathWorkspace?.company.id || reduxWorkspace?.id || lastWorkspaceId || ALMANAC_CORE_WORKSPACE_ID,
  )
  const workspace = useWorkspace(workspaceId)

  const onCustomPath = useIsCustomPath()

  // set workspace id when workspace params change
  useEffect(() => {
    if (articleMeta?.company_id) {
      setWorkspaceId(articleMeta.company_id)
    } else if (pathWorkspace?.company.id) {
      setWorkspaceId(pathWorkspace?.company.id)
    } else if (reduxWorkspace) {
      setWorkspaceId(reduxWorkspace.id)
    } else if (lastWorkspaceId) {
      setWorkspaceId(lastWorkspaceId)
    } else if (defaultDomainWorkspacePathsMatches) {
      setWorkspaceId(ALMANAC_CORE_WORKSPACE_ID)
    }
  }, [
    articleMeta?.company_id,
    pathWorkspace?.company.id,
    reduxWorkspace,
    lastWorkspaceId,
    defaultDomainWorkspacePathsMatches,
  ])

  // redirect to custom subdomain if present and not on a core route
  useEffect(() => {
    // Figure out the workspace from the path (it is an async call for folders and docs)
    if (
      !resolved ||
      workspace?.id == null ||
      // Do not redirect a custom path until the article loads (https://helpcenter.almanac.io/learn)
      ((onCustomPath || docRouteMatch) && articleMeta == null) ||
      // This workspace is not the same as the article, let the useEffect above fix it
      ((onCustomPath || docRouteMatch) && articleMeta?.company_id !== workspace.id)
    ) {
      return
    }

    if (!defaultDomainWorkspacePathsMatches) {
      if (workspace?.workspace_domain?.subdomain) {
        // Redirecting to a subdomain is simple, the auth is shared
        redirectToSubdomainIfNecessary(workspace.workspace_domain)
      } else if (workspace?.workspace_domain && isCertified(workspace.workspace_domain)) {
        // Redirecting to a custom domain can force another login
        redirectToCustomDomainIfNecessary(workspace.workspace_domain)
      } else if (workspace && !!auth) {
        // Redirecting back to almanac.io is easy if we are in a subdomain
        // but it may require another login if we are in a custom domain
        redirectToFrontendDomainIfNecessary()
      }
    } else {
      redirectToFrontendDomainIfNecessary()
    }
  }, [
    articleMeta,
    auth,
    defaultDomainWorkspacePathsMatches,
    docRouteMatch,
    resolved,
    location.pathname,
    onCustomPath,
    workspace,
  ])

  useEffect(() => {
    if (!auth || !workspace) {
      return
    }

    if (!doesAuthWorkspaceMatch(auth, workspace))
      dispatch(
        authMerge({
          auth: {
            last_workspace_id: workspace.id,
            company_id: workspace.id,
            company_key: workspace.key,
            previous_workspace_id:
              workspace.id !== ALMANAC_CORE_WORKSPACE_ID ? workspace.id : auth.previous_workspace_id,
            company_name: workspace.name,
            admin: workspace.admin,
            paying: workspace.paying,
            show_portal: workspace.show_portal,
          },
        }),
      )
  }, [auth, workspace, dispatch])

  // set the current workspace id and auth for legacy components
  useEffect(() => {
    if (!loggedIn || !workspaceId || !resolved) return

    dispatch(currentWorkspaceIdSet(workspace?.id ?? ALMANAC_CORE_WORKSPACE_ID))
  }, [dispatch, workspaceId, workspace?.id, loggedIn, resolved])

  // get workspaces fresh from the server
  useEffect(() => {
    if (!resolved) {
      if (loggedIn) {
        client.getUserWorkspaces().then((e) => {
          if (e.type === SUCCESS_RESPONSE_TYPE) {
            dispatch(workspacesSet(e.data.workspaces))
          } else {
            dispatch(workspacesSet([ALMANAC_CORE_WORKSPACE]))
          }
          setResolved(true)
        })
      } else {
        dispatch(workspacesSet([ALMANAC_CORE_WORKSPACE]))
        setResolved(true)
      }
    }
  }, [dispatch, resolved, loggedIn])

  if (!resolved) {
    return { workspaceId, workspace: undefined, resolved }
  }

  return { workspaceId: workspace?.id ?? ALMANAC_CORE_WORKSPACE_ID, workspace, resolved }
}
